import React, { useState } from 'react';
import { Link } from "react-router-dom";
import LocalizedText from "../presentational/LocalizedText";
import { Button, Checkbox, FormGroup, Input, InputGroup, Intent } from '@careevolution/design-system';
import useTranslate from "../../hooks/useTranslate";
import './LoginPassword.css'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface LoginPasswordProps {
  isLoading: boolean;
  emailOrPhone: string;
  rememberMe: boolean;
  serverError: string;
  notYouLink: string;
  isValidPassword: (password: string) => boolean;
  finishLogin: (password: string) => void;
  forgotPassword: () => void;
  setEmailOrPhone: (emailOrPhone: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
}

const LoginPassword: React.FC<LoginPasswordProps> = ({
  isLoading,
  emailOrPhone,
  rememberMe,
  serverError,
  notYouLink,
  finishLogin,
  forgotPassword,
  isValidPassword,
  setEmailOrPhone,
  setRememberMe,
}) => {
  const [password, setPassword] = useState('')
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const translations = useTranslate(["participant-login-password-label", "participant-login-password-placeholder", "participant-login-remember-me", "participant-login-next-label"])

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrPhone(e.target.value)
  }

  const handleChangeRememberMe = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked)
  }

  const handleNotYouClick = () => {
    setEmailOrPhone('')
    setRememberMe(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishLogin(password)
    return false
  }

  return (
    <form className="login-action login-password" onSubmit={handleSubmit}>
      <p className="personalized-welcome-back">
        <span className="personalized-welcome-back-title"><LocalizedText textKey="participant-login-login-password-title" /></span>
        <span className="personalized-welcome-back-userid"> {emailOrPhone}</span>
      </p>
      <Link className="not-you-link" to={notYouLink} onClick={handleNotYouClick}><LocalizedText textKey="participant-login-not-you-label" /></Link>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <FormGroup className="login-field" label={translations["participant-login-password-label"]}>
        <input type="text" id="username" value={emailOrPhone} onChange={handleChangeEmailOrPhone} onInput={handleChangeEmailOrPhone} autoComplete="username" hidden />
        <InputGroup>
          <Input
            onChange={handleChangePassword}
            autoComplete="current-password"
            className="autofocus"
            disabled={isLoading}
            id="password"
            type={unmaskPassword ? 'text' : 'password'}
            value={password}
            placeholder={translations["participant-login-password-placeholder"]}
            fullWidth={true}
            htmlAttributes={{
              onInput: handleChangePassword,
              maxLength: 64,
              autoFocus: true,
            }}
          />
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </InputGroup>
      </FormGroup>
      <Checkbox
        className="login-field checkbox-field"
        id="rememberMe"
        checked={rememberMe}
        onChange={handleChangeRememberMe}
        label={translations["participant-login-remember-me"]}
      />
      <Button
        onClick={handleSubmit}
        disabled={!isValidPassword(password) || isLoading}
        className="primary-action"
        intent={Intent.primary}
        width="100%"
        loading={isLoading}
        type='submit'
      >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
      <Button
        className="forgot-password-button"
        onClick={forgotPassword}
        width='100%'
        intent={Intent.secondary}
        variant='minimal'
      >
        <LocalizedText textKey='participant-login-forgot-password-label' />
      </Button>
    </form>
  )
}

export default LoginPassword